import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Switch,
  IconButton,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Code as JsonIcon,
  GridOn as ExcelIcon,
  Email as EmailIcon,
  Description as ReportIcon,
  Image as ImageIcon,
  TableChart as ChartIcon,
} from '@mui/icons-material';
import { useAppSelector, selectAnalysisResults, selectExportSettings, updateExportSettings } from '../../store';
import { PropertyAnalysisExportFormat, PropertyAnalysisExportOptions } from '../../types/property';

interface PropertyAnalysisExportProps {
  open: boolean;
  onClose: () => void;
}

const PropertyAnalysisExport: React.FC<PropertyAnalysisExportProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const results = useAppSelector(selectAnalysisResults);
  const exportSettings = useAppSelector(selectExportSettings);
  
  const [exportOptions, setExportOptions] = useState<PropertyAnalysisExportOptions>({
    format: 'pdf',
    sections: ['property', 'pricing', 'cma', 'neighborhood', 'negotiation', 'floorPlan', 'listingDescription', 'clientConsultation'],
    includeRawData: false,
    includeVisualizations: true,
  });
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('Property Analysis Report');
  const [emailMessage, setEmailMessage] = useState('');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [customReportTitle, setCustomReportTitle] = useState('Property Analysis Report');

  // Available sections for export
  const availableSections = [
    { key: 'property', label: 'Property Data', description: 'Basic property information' },
    { key: 'pricing', label: 'Pricing Strategy', description: 'Market positioning and pricing' },
    { key: 'cma', label: 'Market Analysis', description: 'Comparative market analysis' },
    { key: 'neighborhood', label: 'Neighborhood', description: 'Local area insights' },
    { key: 'negotiation', label: 'Negotiation Support', description: 'Deal strategies' },
    { key: 'floorPlan', label: 'Floor Plan', description: 'Layout analysis' },
    { key: 'listingDescription', label: 'Listing Description', description: 'Marketing content analysis' },
    { key: 'clientConsultation', label: 'Client Consultation', description: 'Consultation preparation' },
    { key: 'leadQualification', label: 'Lead Qualification', description: 'Lead scoring and insights' },
    { key: 'transactionManagement', label: 'Transaction Management', description: 'Deal management insights' },
    { key: 'preTourPrep', label: 'Pre-Tour Prep', description: 'Tour preparation checklist' },
    { key: 'clientMatch', label: 'Client Match', description: 'Client matching analysis' },
    { key: 'buyerSentiment', label: 'Buyer Sentiment', description: 'AI sentiment analysis' },
    { key: 'agentOrchestrator', label: 'Agent Orchestrator', description: 'Meta-agent coordination' },
  ];

  // Format options
  const formatOptions = [
    { value: 'pdf', label: 'PDF Report', icon: <PdfIcon />, description: 'Professional report format' },
    { value: 'json', label: 'JSON Data', icon: <JsonIcon />, description: 'Raw data for integration' },
    { value: 'csv', label: 'CSV Spreadsheet', icon: <CsvIcon />, description: 'Tabular data format' },
    { value: 'xlsx', label: 'Excel Spreadsheet', icon: <ExcelIcon />, description: 'Advanced spreadsheet format' },
  ];

  const handleSectionToggle = (sectionKey: string) => {
    setExportOptions(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionKey)
        ? prev.sections.filter(s => s !== sectionKey)
        : [...prev.sections, sectionKey]
    }));
  };

  const handleSelectAllSections = () => {
    setExportOptions(prev => ({
      ...prev,
      sections: availableSections.map(s => s.key)
    }));
  };

  const handleClearAllSections = () => {
    setExportOptions(prev => ({
      ...prev,
      sections: []
    }));
  };

  const handleExport = async () => {
    if (exportOptions.sections.length === 0) {
      setExportError('Please select at least one section to export');
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      let exportType = 'standard';
      
      // Determine export type based on active tab
      if (activeTab === 1) exportType = 'pdf';
      else if (activeTab === 2) exportType = 'email';
      else if (activeTab === 3) exportType = 'custom';

      // Simulate export process with different types
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // TODO: Implement actual export logic based on type
      console.log('Exporting with options:', {
        ...exportOptions,
        exportType,
        emailRecipients: activeTab === 2 ? emailRecipients : undefined,
        emailSubject: activeTab === 2 ? emailSubject : undefined,
        emailMessage: activeTab === 2 ? emailMessage : undefined,
        customTitle: activeTab === 3 ? customReportTitle : undefined,
        includeCharts,
      });
      
      // Show success message based on export type
      let successMessage = 'Export completed successfully!';
      if (exportType === 'pdf') successMessage = 'PDF report generated successfully!';
      else if (exportType === 'email') successMessage = 'Email report sent successfully!';
      else if (exportType === 'custom') successMessage = 'Custom report created successfully!';
      
      // Update export settings in store
      // dispatch(updateExportSettings({
      //   format: exportOptions.format,
      //   sections: exportOptions.sections,
      // }));
      
      // Close modal on success
      onClose();
      
    } catch (error: any) {
      setExportError(error.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Export Property Analysis</Typography>
          <IconButton
            onClick={handleClose}
            disabled={isExporting}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose export format and select which sections to include in your report.
          </Typography>

          {/* Export Tabs */}
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Export Options" icon={<DownloadIcon />} />
            <Tab label="PDF Report" icon={<PdfIcon />} />
            <Tab label="Email Report" icon={<EmailIcon />} />
            <Tab label="Custom Report" icon={<ReportIcon />} />
          </Tabs>

          {/* Export Options Tab */}
          {activeTab === 0 && (
            <>
              {/* Export Format Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Export Format</InputLabel>
                <Select
                  value={exportOptions.format}
                  onChange={(e) => setExportOptions(prev => ({ 
                    ...prev, 
                    format: e.target.value as PropertyAnalysisExportFormat 
                  }))}
                  label="Export Format"
                >
                  {formatOptions.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {format.icon}
                        <Box>
                          <Typography variant="body1">{format.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Section Selection */}
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Analysis Sections</Typography>
                  <Box>
                    <Button size="small" onClick={handleSelectAllSections} sx={{ mr: 1 }}>
                      Select All
                    </Button>
                    <Button size="small" onClick={handleClearAllSections}>
                      Clear All
                    </Button>
                  </Box>
                </Box>

                <FormGroup>
                  <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
                    {availableSections.map((section) => (
                      <FormControlLabel
                        key={section.key}
                        control={
                          <Checkbox
                            checked={exportOptions.sections.includes(section.key)}
                            onChange={() => handleSectionToggle(section.key)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{section.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {section.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                </FormGroup>
              </Box>

              {/* Additional Options */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Additional Options</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOptions.includeRawData}
                        onChange={(e) => setExportOptions(prev => ({ 
                          ...prev, 
                          includeRawData: e.target.checked 
                        }))}
                      />
                    }
                    label="Include raw data and metadata"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOptions.includeVisualizations}
                        onChange={(e) => setExportOptions(prev => ({ 
                          ...prev, 
                          includeVisualizations: e.target.checked 
                        }))}
                      />
                    }
                    label="Include charts and visualizations"
                  />
                </FormGroup>
              </Box>
            </>
          )}

          {/* PDF Report Tab */}
          {activeTab === 1 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>PDF Report Settings</Typography>
              
              <TextField
                fullWidth
                label="Report Title"
                value={customReportTitle}
                onChange={(e) => setCustomReportTitle(e.target.value)}
                sx={{ mb: 2 }}
              />

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeCharts}
                      onChange={(e) => setIncludeCharts(e.target.checked)}
                    />
                  }
                  label="Include interactive charts and graphs"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeVisualizations}
                      onChange={(e) => setExportOptions(prev => ({ 
                        ...prev, 
                        includeVisualizations: e.target.checked 
                      }))}
                    />
                  }
                  label="Include property images and floor plans"
                />
              </FormGroup>
            </Box>
          )}

          {/* Email Report Tab */}
          {activeTab === 2 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Email Report Settings</Typography>
              
              <TextField
                fullWidth
                label="Recipients (comma-separated emails)"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Subject Line"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                multiline
                rows={3}
                placeholder="Add a personal message to accompany the report..."
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={exportOptions.includeVisualizations}
                    onChange={(e) => setExportOptions(prev => ({ 
                      ...prev, 
                      includeVisualizations: e.target.checked 
                    }))}
                  />
                }
                label="Include report as PDF attachment"
              />
            </Box>
          )}

          {/* Custom Report Tab */}
          {activeTab === 3 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Custom Report Builder</Typography>
              
              <TextField
                fullWidth
                label="Report Name"
                value={customReportTitle}
                onChange={(e) => setCustomReportTitle(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Build a custom report with specific sections and formatting options.
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeCharts}
                      onChange={(e) => setIncludeCharts(e.target.checked)}
                    />
                  }
                  label="Include executive summary"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeRawData}
                      onChange={(e) => setExportOptions(prev => ({ 
                        ...prev, 
                        includeRawData: e.target.checked 
                      }))}
                    />
                  }
                  label="Include data tables and raw metrics"
                />
              </FormGroup>
            </Box>
          )}

          {/* Error Display */}
          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {exportError}
            </Alert>
          )}

          {/* Selected Sections Summary */}
          {exportOptions.sections.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected sections ({exportOptions.sections.length}):
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {exportOptions.sections.map((sectionKey) => {
                  const section = availableSections.find(s => s.key === sectionKey);
                  return section ? (
                    <Chip
                      key={sectionKey}
                      label={section.label}
                      size="small"
                      variant="outlined"
                      onDelete={() => handleSectionToggle(sectionKey)}
                    />
                  ) : null;
                })}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isExporting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={isExporting || exportOptions.sections.length === 0}
          variant="contained"
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          {isExporting ? 'Exporting...' : 'Export Analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyAnalysisExport;
